import { useEffect, useState } from 'react';
import {
  AgreementReadinessCategoryId,
  AgreementReadinessErrorCode,
  InProgressFormModelFormType,
  useGetAgreementReadiness,
  useGetCampaign,
  useInvalidateUseGetAgreementReadiness,
  useInvalidateUseGetCampaign,
  type AgreementReadinessEditTarget,
} from '@urbanx/agx.campaigns.client';
import {
  AgxButton,
  AgxModal,
  AgxTypography,
  AgxAnimatedToggleLoader,
} from '@urbanx/agx.ui-components2';
import { resolveReadinessTarget } from './readinessNavigation';
import { AgreementConversion, ExistingAgreement, type OpenAgreement } from './AgreementConversion';
import { classifyReadinessError } from './readinessErrors';
import * as styles from './AgreementReadinessDialog.css';

export interface AgreementReadinessDialogProps {
  proposalId: string;
  campaignId: string;
  openAgreement: OpenAgreement;
  isOpen: boolean;
  setIsDialogOpen: (isOpen: boolean) => void;
}

export const AgreementReadinessDialog = ({
  proposalId,
  campaignId,
  openAgreement,
  isOpen,
  setIsDialogOpen,
}: AgreementReadinessDialogProps) => {
  const [selection, setSelection] = useState(() => {
    const formId = sessionStorage.getItem(`agreement-readiness-draft:${campaignId}`);
    return formId ? { campaignId, formId } : undefined;
  });
  return (
    <AgxModal
      id="agreement-readiness"
      className={styles.dialog}
      title="Convert to Agreement"
      type="single"
      minWidth="320px"
      maxWidth="560px"
      maxHeight="65vh"
      variant="withoutDivider"
      actionButtonContainerStyle="withDivider"
      isOpen={isOpen}
      setIsDialogOpen={setIsDialogOpen}
      content={
        isOpen && (
          <div className={styles.content}>
            <ReadinessSession
              key={`${campaignId}:${proposalId}`}
              campaignId={campaignId}
              proposalId={proposalId}
              candidate={selection?.campaignId === campaignId ? selection.formId : undefined}
              openAgreement={openAgreement}
              remember={(formId) => {
                if (formId)
                  sessionStorage.setItem(`agreement-readiness-draft:${campaignId}`, formId);
                else sessionStorage.removeItem(`agreement-readiness-draft:${campaignId}`);
                setSelection(formId ? { campaignId, formId } : undefined);
              }}
              close={() => setIsDialogOpen(false)}
            />
          </div>
        )
      }
      actionButtons={
        <AgxButton
          id="readiness-close"
          text="Keep editing proposal"
          variant="hollow"
          onClick={() => setIsDialogOpen(false)}
        />
      }
    />
  );
};

const labels: Record<AgreementReadinessCategoryId, string> = {
  Property: 'Property',
  Vendors: 'Vendors',
  Pricing: 'Pricing',
  Marketing: 'Marketing',
  Commission: 'Commission',
  StateDisclosures: 'State disclosures',
};
const statusLabel = (status: string) => (status === 'NeedsReview' ? 'Needs review' : status);

const ReadinessSession = ({
  campaignId,
  proposalId,
  candidate,
  openAgreement,
  remember,
  close,
}: {
  campaignId: string;
  proposalId: string;
  candidate?: string;
  openAgreement: OpenAgreement;
  remember: (formId?: string) => void;
  close: () => void;
}) => {
  const [formId, setFormId] = useState<string>();
  const [choosing, setChoosing] = useState(!!candidate);
  const select = (selected?: string) => {
    remember(selected);
    setFormId(selected);
    setChoosing(false);
  };
  return (
    <>
      <AgxTypography id="readiness-description" type="body" size="small">
        Review agreement readiness. Nothing is created until you confirm an agreement draft.
      </AgxTypography>
      {choosing ? (
        <DraftSelector campaignId={campaignId} candidate={candidate} select={select} />
      ) : (
        <ReadinessResult
          key={formId ?? 'campaign'}
          campaignId={campaignId}
          proposalId={proposalId}
          formId={formId}
          openAgreement={openAgreement}
          close={close}
          select={select}
          choose={() => {
            remember(undefined);
            setChoosing(true);
          }}
          clear={() => select(undefined)}
        />
      )}
    </>
  );
};

const Loading = () => (
  <div role="status">
    <AgxAnimatedToggleLoader label="Loading readiness…" />
  </div>
);

const ReadinessResult = ({
  campaignId,
  proposalId,
  formId,
  openAgreement,
  close,
  choose,
  clear,
  select,
}: {
  campaignId: string;
  proposalId: string;
  formId?: string;
  openAgreement: OpenAgreement;
  close: () => void;
  choose: () => void;
  clear: () => void;
  select: (formId?: string) => void;
}) => {
  const [enabled, setEnabled] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const invalidate = useInvalidateUseGetAgreementReadiness();
  const query = useGetAgreementReadiness(campaignId, formId, enabled);
  const [refresh] = useState(() => () => invalidate(campaignId, formId));
  useEffect(() => {
    let active = true;
    // Invalidate while disabled so even an infinite-stale cached result requires a new read.
    void refresh().then(() => {
      if (active) setEnabled(true);
    });
    return () => {
      active = false;
    };
  }, [refresh]);
  const retry = async () => {
    setRefreshing(true);
    try {
      await refresh();
    } finally {
      setRefreshing(false);
    }
  };
  if (!enabled || refreshing || query.isFetching || query.isPending) return <Loading />;
  if (query.isError) {
    const error = classifyReadinessError(query.error);
    if (error.code === AgreementReadinessErrorCode.DraftSelectionRequired)
      return <DraftSelector campaignId={campaignId} select={select} />;
    return (
      <>
        <AgxTypography id="readiness-error" type="body" size="small" role="alert">
          {error.message}
        </AgxTypography>
        {error.retry && (
          <AgxButton id="readiness-retry" text="Retry" variant="hollow" onClick={retry} />
        )}
        {error.code === AgreementReadinessErrorCode.AlreadyConverted && (
          <ExistingAgreement
            campaignId={campaignId}
            formId={formId}
            openAgreement={openAgreement}
            close={close}
          />
        )}
        {(error.code === AgreementReadinessErrorCode.FormNotFound ||
          error.code === AgreementReadinessErrorCode.InvalidFormType) && (
          <>
            <AgxButton
              id="readiness-reselect"
              text="Choose another draft"
              variant="hollow"
              onClick={choose}
            />
            <AgxButton
              id="readiness-clear"
              text="Review without selection"
              variant="hollow"
              onClick={clear}
            />
          </>
        )}
      </>
    );
  }
  if (!query.data) return <Loading />;
  return (
    <>
      <div className={styles.categories}>
        {query.data.categories.map((category) => (
          <details
            key={category.id}
            className={styles.category}
            open={category.remainingWork.length > 0}
          >
            <summary className={styles.summary}>
              <AgxTypography id={`readiness-${category.id}`} type="body_ui_strong" size="small">
                {labels[category.id]}
              </AgxTypography>
              <span className={styles.status} data-status={category.status}>
                <AgxTypography type="tag" size="small" color="inherit">
                  {statusLabel(category.status)}
                </AgxTypography>
              </span>
            </summary>
            <div className={styles.details}>
              <EditingTarget
                target={category.editTarget}
                campaignId={campaignId}
                proposalId={proposalId}
                label={`Edit ${labels[category.id].toLowerCase()}`}
                close={close}
              />
              {category.remainingWork.map((item) => (
                <div key={item.requirementId} className={styles.workItem}>
                  <AgxTypography
                    id={`readiness-${item.requirementId}`}
                    type="body_ui_strong"
                    size="small"
                  >
                    {statusLabel(item.status)}
                  </AgxTypography>
                  {item.messages.map((message, index) => (
                    <AgxTypography
                      key={index}
                      id={`readiness-${item.requirementId}-${index}`}
                      type="body"
                      size="small"
                    >
                      {message}
                    </AgxTypography>
                  ))}
                  <EditingTarget
                    target={item.editTarget}
                    campaignId={campaignId}
                    proposalId={proposalId}
                    label="Edit details"
                    close={close}
                  />
                </div>
              ))}
            </div>
          </details>
        ))}
      </div>
      <AgreementConversion
        campaignId={campaignId}
        formId={query.data.formId ?? undefined}
        campaignETag={query.data.campaignETag}
        onReviewAgain={() => void retry()}
        openAgreement={openAgreement}
        close={close}
      />
    </>
  );
};

const DraftSelector = ({
  campaignId,
  candidate,
  select,
}: {
  campaignId: string;
  candidate?: string;
  select: (formId?: string) => void;
}) => {
  const [enabled, setEnabled] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [choice, setChoice] = useState('');
  const invalidate = useInvalidateUseGetCampaign();
  const query = useGetCampaign(campaignId, enabled);
  const [refresh] = useState(() => () => invalidate(campaignId));
  const forms =
    query.data?.inProgressForms?.filter(
      (form) => form.formType === InProgressFormModelFormType.ListingProposal && form.formId
    ) ?? [];
  useEffect(() => {
    let active = true;
    void refresh().then(() => {
      if (active) setEnabled(true);
    });
    return () => {
      active = false;
    };
  }, [refresh]);
  const candidateIsCurrent = forms.some((form) => form.formId === candidate);
  useEffect(() => {
    if (enabled && !query.isFetching && !query.isPending && !query.isError && candidate) {
      if (candidateIsCurrent) select(candidate);
      else sessionStorage.removeItem(`agreement-readiness-draft:${campaignId}`);
    }
  }, [
    enabled,
    query.isFetching,
    query.isPending,
    query.isError,
    candidateIsCurrent,
    candidate,
    campaignId,
    select,
  ]);
  if (!enabled || refreshing || query.isFetching || query.isPending) return <Loading />;
  if (query.isError)
    return (
      <>
        <AgxTypography id="readiness-drafts-error" type="body" size="small" role="alert">
          Unable to load saved drafts. Please retry.
        </AgxTypography>
        <AgxButton
          id="readiness-drafts-retry"
          text="Retry"
          variant="hollow"
          onClick={async () => {
            setRefreshing(true);
            try {
              await refresh();
            } finally {
              setRefreshing(false);
            }
          }}
        />
      </>
    );
  return (
    <div className={styles.details}>
      <AgxTypography id="readiness-draft-message" type="body" size="small">
        {candidate
          ? 'The previous draft is no longer available. Choose a current draft.'
          : 'Choose a saved Listing Proposal draft to review.'}
      </AgxTypography>
      <label htmlFor="readiness-draft">Listing Proposal draft</label>
      <select
        id="readiness-draft"
        className={styles.select}
        value={choice}
        onChange={(event) => setChoice(event.target.value)}
      >
        <option value="">Choose a draft</option>
        {forms.map((form) => (
          <option key={form.formId} value={form.formId}>
            Draft {form.formId}
          </option>
        ))}
      </select>
      <AgxButton
        id="readiness-select"
        text="Review selected draft"
        variant="primary"
        disabled={!forms.some((form) => form.formId === choice)}
        onClick={() => select(choice)}
      />
      {forms.length === 0 && (
        <AgxTypography id="readiness-no-drafts" type="body" size="small">
          No saved Listing Proposal drafts are available.
        </AgxTypography>
      )}
      {candidate && (
        <AgxButton
          id="readiness-without-draft"
          text="Review without selection"
          variant="hollow"
          onClick={() => select(undefined)}
        />
      )}
    </div>
  );
};

const EditingTarget = ({
  target,
  campaignId,
  proposalId,
  label,
  close,
}: {
  target: AgreementReadinessEditTarget;
  campaignId: string;
  proposalId: string;
  label: string;
  close: () => void;
}) => {
  const resolved = resolveReadinessTarget(target, { campaignId, proposalId });
  return resolved.href ? (
    <a className={styles.link} href={resolved.href} onClick={close}>
      <AgxTypography type="hyperlink" size="small" color="inherit">
        {label}
      </AgxTypography>
    </a>
  ) : (
    <AgxTypography type="body" size="small">
      {resolved.message}
    </AgxTypography>
  );
};
